import { useCallback, useEffect, useRef, useState } from "react";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import { useSelector } from "react-redux";
import { Button, Container } from "react-bootstrap";
import { AdvanceFilter, FilterBody, FilterHeaderStyle } from "@cleanup/components/AdvancedFilters/FilterStyle";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import CommonMapReact from "@cleanup/containers/CommonMapReact/CommonMapReact";
import L from "leaflet";
import AlertSwitch from "@cleanup/pages/AlertRule/AlertSwitch";
import { RootState } from "@/types/RootState";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterValue {
  includeEmpty?: boolean;
  includeNoEmpty?: boolean;
  xmin?: number;
  ymin?: number;
  xmax?: number;
  ymax?: number;
}

interface FilterConfig {
  switchOn?: boolean;
}

interface FilterData {
  filterValue?: FilterValue;
  config?: FilterConfig;
}

interface Descriptor {
  description?: string;
}

interface BoundFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData) => void;
  disabled?: boolean;
}

interface DataState {
  searchText: string;
  includeEmpty: boolean;
  includeNoEmpty: boolean;
}

const BoundFilter: React.FC<BoundFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false
}) => {
  const { isMobile } = useSelector((state: RootState) => state.all);
  const [isShow, setShow] = useState(false);
  const map = useRef<L.Map | null>(null);

  const [dataState, setDataState] = useState<DataState>({
    searchText: "",
    includeEmpty: false,
    includeNoEmpty: false,
  });

  const [active, setActive] = useState(false);

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  const originalBoundsRef = useRef<L.LatLngBounds | null>(null);
  const rectangleRef = useRef<L.Rectangle | null>(null);

  const hasUserInteracted = useRef(false);
  const isMapReady = useRef(false);

  useEffect(() => {
    const { filterValue, config } = data;

    if (config && config.switchOn !== undefined && config.switchOn !== null) {
      setActive(config.switchOn);
    }

    if (isShow && map.current) {
      map.current.invalidateSize();

      setTimeout(() => {
        if (
          filterValue &&
          filterValue.xmin !== undefined &&
          filterValue.ymin !== undefined &&
          filterValue.xmax !== undefined &&
          filterValue.ymax !== undefined
        ) {
          const bounds = L.latLngBounds(
            L.latLng(filterValue.xmin, filterValue.ymin),
            L.latLng(filterValue.xmax, filterValue.ymax)
          );
          originalBoundsRef.current = bounds;

          const leafletMap = map.current;

          if (leafletMap) {
            if (rectangleRef.current) {
              leafletMap.removeLayer(rectangleRef.current);
            }
            if (originalBoundsRef.current && active) {
              const bounds = originalBoundsRef.current;

              rectangleRef.current = L.rectangle(bounds, {
                color: "#2785db",
                weight: 1,
                fillColor: "#f7f3a1",
                fillOpacity: 0.3,
                interactive: false,
              }).addTo(leafletMap);
            }

            if (map.current) {
              map.current.fitBounds(bounds);
            }
          }
        } else {
          const worldBounds = L.latLngBounds(
            L.latLng(-90, -180),
            L.latLng(90, 180)
          );
          if (map.current) {
            map.current.fitBounds(worldBounds, {
              padding: [10, 10],
              maxZoom: 0,
            });
          }
          originalBoundsRef.current = worldBounds;
        }
      }, 100);
    }

    let _dataState: DataState = {
      searchText: "",
      includeEmpty: false,
      includeNoEmpty: false,
    };

    if (filterValue) {
      _dataState = { ..._dataState, ...filterValue };
    }

    setDataState(_dataState);
  }, [isShow, data, active]);

  useEffect(() => {
    const leafletMap = map.current;
    if (!leafletMap) return;

    const markUserInteraction = () => {
      if (isMapReady.current) {
        hasUserInteracted.current = true;
      }
    };

    leafletMap.on("zoom", markUserInteraction);
    leafletMap.on("move", markUserInteraction);

    const markMapReady = () => {
      isMapReady.current = true;
      leafletMap.off("moveend", markMapReady);
    };

    leafletMap.on("moveend", markMapReady);

    return () => {
      leafletMap.off("zoom", markUserInteraction);
      leafletMap.off("move", markUserInteraction);
      leafletMap.off("moveend", markMapReady);
    };
  }, [isShow]);

  const getData = useCallback(
    (checked: boolean) => {
      const bounds = originalBoundsRef.current;

      const newData = JSON.parse(JSON.stringify(data)) as FilterData;

      if (bounds) {
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();

        newData.filterValue = {
          includeEmpty: dataState.includeEmpty,
          includeNoEmpty: dataState.includeNoEmpty,
          xmin: southWest.lat,
          ymin: southWest.lng,
          xmax: northEast.lat,
          ymax: northEast.lng,
        };
      } else {
        newData.filterValue = {
          includeEmpty: dataState.includeEmpty,
          includeNoEmpty: dataState.includeNoEmpty,
        };
      }

      newData.config = { switchOn: checked };

      return newData;
    },
    [data, dataState]
  );

  function isFilterChanged(oldDataFilter: FilterValue, newDataFilter: FilterValue): boolean {
    const keys = new Set([
      ...Object.keys(oldDataFilter),
      ...Object.keys(newDataFilter),
    ]);

    for (const key of keys) {
      if (oldDataFilter[key as keyof FilterValue] !== newDataFilter[key as keyof FilterValue]) {
        return true;
      }
    }

    return false;
  }

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData(active);
    const newDataFilter = newData.filterValue || {};
    return isFilterChanged(oldDataFilter, newDataFilter);
  }, [data, getData, active]);

  const refWrapper = useOuterClick(() => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData(active));
        }
      }
    }
  });

  const onClickButton = () => {
    hasUserInteracted.current = false;
    isMapReady.current = false;

    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData(active));
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onAppliedFilter = (checked: boolean) => {
    if (onChange) {
      onChange(getData(checked));
    }
  };

  const onSwitchChange = (checked: boolean) => {
    setActive(checked);
    onAppliedFilter(checked);
  };

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        <span>{translate(LanguageKey.map_key)}</span>:{" "}
        <FilterHeaderStyle>
          {active ? translate(LanguageKey.map_on_key) : translate(LanguageKey.map_off_key)}
        </FilterHeaderStyle>
      </Button>
      <FilterBody
        ref={popupRef}
        style={{
          display: isShow ? "block" : "none",
          ...popupStyleRef.current,
        }}
      >
        <Container className="pb-3">
          <div className="mt-3 d-flex flex-row flex-wrap">
            <div className="flex-grow-1 align-self-center">
              <AlertSwitch
                active={active}
                id={"bound"}
                onSwitchChange={onSwitchChange}
                disabled={false}
              />
            </div>

            <div className="mb-1 flex-grow-1 align-self-center">
              <div className="float-end"></div>
            </div>
          </div>

          <CommonMapReact
            mapRef={map}
            width={isMobile ? "100%" : "40vh"}
            height={isMobile ? "40vh" : "40vh"}
            options={{
              hideCurrentLocation: true,
              hideOverview: true,
              hideZoomControl: true,
              hideFullScreen: true,
              searchBox: false,
              showGeozoneLayer: false,
              showNetworkLayer: false,
            }}
            disableInteractions={true}
          />
        </Container>
      </FilterBody>
    </AdvanceFilter>
  );
};

export default BoundFilter;
